(() => {
  if (customElements.get('herbl-how-it-works') && customElements.get('herbl-liver-progression')) return;
  let playerPromise;
  const dataPromises = new Map();
  function loadPlayer(url) {
    if (window.lottie) return Promise.resolve(window.lottie);
    if (!playerPromise) {
      playerPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.onload = () => window.lottie ? resolve(window.lottie) : reject(new Error('Animation player unavailable'));
        script.onerror = () => { script.remove(); playerPromise = null; reject(new Error('Animation player unavailable')); };
        document.head.append(script);
      });
    }
    return playerPromise;
  }
  function loadData(url) {
    if (!dataPromises.has(url)) {
      const request = fetch(url).then(response => {
        if (!response.ok) throw new Error('Animation data unavailable');
        return response.json();
      }).catch(error => { dataPromises.delete(url); throw error; });
      dataPromises.set(url, request);
    }
    return dataPromises.get(url);
  }
  function mount(root) {
    const abort = new AbortController();
    const signal = abort.signal;
    const pane = root.closest('.ctb-pane');
    const details = root.closest('details');
    const summary = details?.querySelector(':scope > summary');
    const problemToggle = root.querySelector('[data-local-id="hw-problem-toggle"]');
    const motionReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const controls = [...root.querySelectorAll('.hw-accordion-trigger')];
    const icons = [...root.querySelectorAll('[data-organ-animation],[data-progression-animation]')].map(container => ({container, animation:null, visible:false}));
    const observers = [];
    let active = true;
    let loading = false;
    let ready = false;
    let syncFrame = 0;
    const isActive = () => active && root.isConnected && (!pane || pane.classList.contains('active')) && (!details || details.open) && summary?.getAttribute('aria-expanded') !== 'false' && !root.closest('[hidden]');

  const mechanism = root.querySelector('.hw-symptom-story');
  const flowSvg = mechanism.querySelector('.hw-symptom-beams');
  const symptomPills = [...mechanism.querySelectorAll('[data-symptom]')];
  const symptomRow = mechanism.querySelector('.hw-symptom-row');
  const sequenceIcons = [...mechanism.querySelectorAll('.hw-sequence-icon')];
  const bloodSource = mechanism.querySelector('[data-beam-source]');
  let flowGeometry = '';
  let flowPaths = [];
  let tubeScene = null;
  let flowFrame = 0;
  let flowElapsed = 0;
  let flowPrevious = 0;
  let flowVisible = false;
  const svgNS = 'http://www.w3.org/2000/svg';
  function flowElement(tag, attributes) {
    const el = document.createElementNS(svgNS, tag);
    Object.entries(attributes).forEach(([key, value]) => el.setAttribute(key, value));
    return el;
  }
  function fitSymptomRow() {
    symptomRow.classList.remove('is-two-rows');
    if (symptomRow.classList.contains('hw-symptom-row--compact')) {
      symptomRow.style.fontSize='';
      return;
    }
    symptomRow.style.fontSize='12px';
    const available=symptomRow.getBoundingClientRect().width;
    const needed=()=>symptomPills.reduce((sum,pill)=>sum+pill.getBoundingClientRect().width,0)+9;
    let size=12;
    while(size>11 && needed()>available){size-=.25;symptomRow.style.fontSize=`${size}px`}
    if(needed()>available){symptomRow.classList.add('is-two-rows');symptomRow.style.fontSize='12px'}
  }
  function drawFlow() {
    if(!isActive() || !mechanism.clientWidth || root.closest('[hidden]'))return;
    fitSymptomRow();
    const bounds = mechanism.getBoundingClientRect();
    if (!bounds.width || !bounds.height || root.closest('[hidden]')) return;
    const relativeRect = el => {
      const rect = el.getBoundingClientRect();
      return {x:rect.x-bounds.x, y:rect.y-bounds.y, width:rect.width, height:rect.height};
    };
    const source = relativeRect(bloodSource);
    const organs = sequenceIcons.map(relativeRect);
    const targets = symptomPills.map(relativeRect);
    const key = JSON.stringify([bounds.width,bounds.height,organs,targets]);
    if (key === flowGeometry) return;
    flowGeometry = key;
    flowSvg.replaceChildren();
    flowSvg.setAttribute('viewBox',`0 0 ${bounds.width} ${bounds.height}`);
    const defs = flowElement('defs',{});
    const clipId=root.id+'-toxin-tube-clip';
    const clip=flowElement('clipPath',{id:clipId,clipPathUnits:'userSpaceOnUse'});
    defs.append(clip);flowSvg.append(defs);
    const tubeLayer=flowElement('g',{});flowSvg.append(tubeLayer);
    const y=organs[0].y+organs[0].height*.5;
    organs.slice(0,-1).forEach((organ,index)=>{
      const x1=organ.x+organ.width-2,x2=organs[index+1].x+2;
      const d=`M ${x1} ${y} L ${x2} ${y}`;
      tubeLayer.append(flowElement('path',{d,fill:'none',stroke:'#9eaf8d','stroke-width':9,'stroke-linecap':'round','data-toxin-tube':index}));
      tubeLayer.append(flowElement('path',{d,fill:'none',stroke:'#edf3e3','stroke-width':6.5,'stroke-linecap':'round'}));
      tubeLayer.append(flowElement('path',{d:`M ${x1} ${y-2} L ${x2} ${y-2}`,fill:'none',stroke:'#fcfcf7','stroke-width':1,opacity:.8}));
      clip.append(flowElement('rect',{x:x1,y:y-3,width:x2-x1,height:6,rx:3}));
    });
    const particlesLayer=flowElement('g',{'clip-path':`url(#${clipId})`});flowSvg.append(particlesLayer);
    const startX=organs[0].x+organs[0].width*.5,endX=source.x+source.width*.5;
    const particles=Array.from({length:Math.ceil((endX-startX)/16)},(_,index)=>{
      const particle=flowElement('circle',{r:index%3===0?2:1.65,fill:index%2?'#57793a':'#879d4e',opacity:0,'data-toxin-particle':index});
      particlesLayer.append(particle);return particle;
    });
    tubeScene={startX,endX,y,particles};
    const beamLayer=flowElement('g',{});flowSvg.append(beamLayer);
    const sx=source.x+source.width*.5, sy=source.y+4;
    flowPaths=targets.map((target,index)=>{
      const tx=target.x+target.width*.5,ty=target.y+target.height;
      const bend=Math.max(18,(sy-ty)*.6);
      const d=`M ${sx} ${sy} C ${sx} ${sy-bend}, ${tx} ${ty+16}, ${tx} ${ty}`;
      const halo=flowElement('path',{d,fill:'none',stroke:'#b1ce83','stroke-width':4,'stroke-linecap':'round',opacity:0,'data-symptom-beam':index});
      const core=flowElement('path',{d,fill:'none',stroke:'#638c40','stroke-width':1.25,'stroke-linecap':'round',opacity:0});
      beamLayer.append(halo,core);
      const length=core.getTotalLength();
      [halo,core].forEach(el=>el.setAttribute('stroke-dasharray',`16 ${length+32}`));
      return {halo,core,length,pill:symptomPills[index]};
    });
  }
  function flowAllowed() {
    return isActive() && flowVisible && !motionReduced.matches && !root.closest('[hidden]') && document.visibilityState!=='hidden' && (!problemToggle || problemToggle.getAttribute('aria-expanded')==='true');
  }
  function stopFlow() {
    cancelAnimationFrame(flowFrame);flowFrame=0;flowPrevious=0;
    flowPaths.forEach(({halo,core,pill})=>{
      halo.setAttribute('opacity','0');core.setAttribute('opacity','0');
      pill.style.boxShadow='';pill.style.backgroundColor='';
    });
    tubeScene?.particles.forEach(particle=>particle.setAttribute('opacity','0'));
    mechanism.dataset.flowState=motionReduced.matches?'still':'paused';
  }
  function animateFlow(now) {
    if(!flowAllowed()){stopFlow();return}
    if(flowPrevious)flowElapsed+=Math.min(now-flowPrevious,80);
    flowPrevious=now;
    const t=flowElapsed/1000;
    if(tubeScene){
      const {startX,endX,y,particles}=tubeScene;
      const span=endX-startX;
      particles.forEach((particle,index)=>{
        const x=startX+(t*44+index*span/particles.length)%span;
        particle.setAttribute('cx',String(x));
        particle.setAttribute('cy',String(y+Math.sin(t*2.3+index*1.7)*.65));
        particle.setAttribute('opacity','.88');
      });
    }
    const phase=t%1.8;
    const progress=Math.min(1,phase/1.05);
    const opacity=phase<1.05?Math.sin(progress*Math.PI)*.7:0;
    const hit=phase>=.85?Math.sin((phase-.85)/.95*Math.PI):0;
    flowPaths.forEach(({halo,core,length,pill})=>{
      [halo,core].forEach(el=>el.setAttribute('stroke-dashoffset',String(16-progress*(length+16))));
      halo.setAttribute('opacity',String(opacity*.22));core.setAttribute('opacity',String(opacity));
      pill.style.boxShadow=hit>0?`0 0 0 ${1+hit*2}px rgba(128,163,82,${hit*.18})`:'';
      pill.style.backgroundColor=hit>0?`rgb(${245-hit*17} ${245+hit*3} ${239-hit*32})`:'';
    });
    mechanism.dataset.flowState='playing';
    flowFrame=requestAnimationFrame(animateFlow);
  }
  function playFlow() {
    drawFlow();
    if(!flowAllowed()){stopFlow();return}
    if(flowFrame)return;
    flowPrevious=performance.now();flowFrame=requestAnimationFrame(animateFlow);
  }


    function syncIcons() {
      icons.forEach(({container, animation, visible}) => {
        if (!animation) return;
        const playing = isActive() && visible && !container.closest('[hidden]') && document.visibilityState !== 'hidden' && !motionReduced.matches;
        if (motionReduced.matches) animation.goToAndStop(0, true);
        else if (playing) animation.play();
        else animation.pause();
        container.dataset.iconState = motionReduced.matches ? 'still' : playing ? 'playing' : 'paused';
      });
    }
    async function ensureAnimations() {
      if (ready || loading || !isActive()) return;
      loading = true;
      try {
        const [player, data] = await Promise.all([loadPlayer(root.dataset.playerUrl), loadData(root.dataset.animationUrl)]);
        if (!active || !root.isConnected) return;
        icons.forEach(item => {
          const {container} = item;
          const source = container.dataset.organAnimation ? data.organs[container.dataset.organAnimation] : data.progression[container.dataset.progressionAnimation];
          const animation = player.loadAnimation({container, renderer:'svg', loop:true, autoplay:false, animationData:JSON.parse(JSON.stringify(source)), rendererSettings:{preserveAspectRatio:'xMidYMid meet',progressiveLoad:false}});
          item.animation = animation;
          animation.addEventListener('DOMLoaded', () => { container.dataset.iconReady='true'; syncIcons(); });
          animation.addEventListener('loopComplete', () => { container.dataset.completedLoops=String(Number(container.dataset.completedLoops||0)+1); });
          if (animation.isLoaded) container.dataset.iconReady='true';
        });
        ready = true;
        root.dataset.animationState = 'ready';
        syncIcons();
      } catch (error) {
        root.dataset.animationState = 'unavailable';
        console.warn('herbl how-it-works:', error.message);
      } finally {
        loading = false;
      }
    }
    function sync() {
      if (!active) return;
      if (isActive() && icons.some(icon => icon.visible)) ensureAnimations();
      syncIcons();
      if (flowAllowed()) playFlow(); else stopFlow();
    }
    function scheduleSync() {
      cancelAnimationFrame(syncFrame);
      syncFrame = requestAnimationFrame(() => { syncFrame=0; sync(); });
    }
    function setPanel(button, open, animate=true) {
      const panel = root.querySelector(`[id="${button.getAttribute('aria-controls')}"]`);
      if ((button.getAttribute('aria-expanded')==='true')===open && !panel._cleanup) return;
      const currentHeight = panel.hidden ? 0 : panel.getBoundingClientRect().height;
      if (panel._cleanup) panel._cleanup();
      button.setAttribute('aria-expanded', String(open));
      const settle = () => {
        panel.hidden = !open;
        panel.style.height = open ? 'auto' : '';
        panel.classList.remove('is-height-animating');
        panel._cleanup = null;
        scheduleSync();
      };
      if (!animate || motionReduced.matches) { settle(); return; }
      panel.classList.add('is-height-animating');
      panel.hidden = false;
      panel.style.height = `${currentHeight}px`;
      void panel.offsetHeight;
      panel.style.height = `${open ? panel.querySelector('.hw-accordion-inner').getBoundingClientRect().height : 0}px`;
      const finish = () => { panel.removeEventListener('transitionend', onEnd); clearTimeout(timer); settle(); };
      const onEnd = event => { if (event.target===panel && event.propertyName==='height') finish(); };
      const timer = setTimeout(finish, 450);
      panel._cleanup = () => { panel.removeEventListener('transitionend', onEnd); clearTimeout(timer); };
      panel.addEventListener('transitionend', onEnd);
    }
    controls.forEach(button => button.addEventListener('click', () => {
      const open = button.getAttribute('aria-expanded') !== 'true';
      controls.forEach(other => setPanel(other, other===button && open));
    }, {signal}));

    const iconObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const item = icons.find(icon => icon.container===entry.target);
        item.visible = entry.isIntersecting;
      });
      if (icons.some(icon => icon.visible)) ensureAnimations();
      syncIcons();
    }, {threshold:0});
    icons.forEach(({container}) => iconObserver.observe(container));
    observers.push(iconObserver);
    const flowObserver = new IntersectionObserver(entries => {
      flowVisible=entries[entries.length-1].isIntersecting;
      scheduleSync();
    }, {threshold:0});
    flowObserver.observe(mechanism);
    observers.push(flowObserver);
    const resizeObserver = new ResizeObserver(() => { if (isActive()) { drawFlow(); scheduleSync(); } });
    resizeObserver.observe(mechanism);
    observers.push(resizeObserver);
    const disclosureObserver = new MutationObserver(scheduleSync);
    disclosureObserver.observe(root, {subtree:true, attributes:true, attributeFilter:['hidden','aria-expanded']});
    observers.push(disclosureObserver);
    if (details) {
      const parentDisclosureObserver = new MutationObserver(scheduleSync);
      parentDisclosureObserver.observe(details, {attributes:true, attributeFilter:['open']});
      if (summary) parentDisclosureObserver.observe(summary, {attributes:true, attributeFilter:['aria-expanded']});
      observers.push(parentDisclosureObserver);
    }
    if (pane) {
      const tabObserver = new MutationObserver(() => { if(isActive() && icons.some(icon=>icon.visible)) ensureAnimations(); scheduleSync(); });
      tabObserver.observe(pane, {attributes:true, attributeFilter:['class','hidden']});
      observers.push(tabObserver);
    }
    document.addEventListener('visibilitychange', scheduleSync, {signal});
    window.addEventListener('pageshow', scheduleSync, {signal});
    motionReduced.addEventListener('change', scheduleSync, {signal});
    document.fonts.ready.then(() => { if(active) { drawFlow(); scheduleSync(); } });
    return () => {
      active=false;
      abort.abort();
      cancelAnimationFrame(syncFrame);
      stopFlow();
      observers.forEach(observer=>observer.disconnect());
      icons.forEach(({animation})=>animation?.destroy());
      root.querySelectorAll('.hw-accordion-panel').forEach(panel=>panel._cleanup?.());
    };
  }
  if (!customElements.get('herbl-how-it-works')) customElements.define('herbl-how-it-works', class extends HTMLElement {
    connectedCallback() {
      requestAnimationFrame(() => {
        if (this.isConnected && !this.dispose && this.querySelector('.hw-symptom-story')) this.dispose = mount(this);
      });
    }
    disconnectedCallback() {
      this.dispose?.();
      this.dispose = null;
    }
  });

  function mountProgression(root) {
    const abort = new AbortController();
    const details = root.closest('details');
    const summary = details?.querySelector(':scope > summary');
    const motionReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const icons = [...root.querySelectorAll('[data-progression-animation]')].map(container => ({container, animation: null}));
    let active = true;
    let visible = false;
    let loading = false;
    let ready = false;
    const isVisible = () => active && root.isConnected && visible && (!details || details.open) && summary?.getAttribute('aria-expanded') !== 'false' && document.visibilityState !== 'hidden';
    function sync() {
      if (isVisible() && !ready && !loading) ensureAnimations();
      icons.forEach(({container, animation}) => {
        if (!animation) return;
        if (motionReduced.matches) animation.goToAndStop(0, true);
        else if (isVisible()) animation.play();
        else animation.pause();
        container.dataset.iconState = motionReduced.matches ? 'still' : isVisible() ? 'playing' : 'paused';
      });
    }
    async function ensureAnimations() {
      loading = true;
      root.dataset.animationState = 'loading';
      try {
        const [player, data] = await Promise.all([loadPlayer(root.dataset.playerUrl), loadData(root.dataset.animationUrl)]);
        if (!active || !root.isConnected) return;
        icons.forEach(item => {
          const {container} = item;
          const animation = player.loadAnimation({container, renderer: 'svg', loop: true, autoplay: false, animationData: JSON.parse(JSON.stringify(data.progression[container.dataset.progressionAnimation])), rendererSettings: {preserveAspectRatio: 'xMidYMid meet', progressiveLoad: false}});
          item.animation = animation;
          animation.addEventListener('DOMLoaded', () => { container.dataset.iconReady = 'true'; sync(); });
          animation.addEventListener('loopComplete', () => { container.dataset.completedLoops = String(Number(container.dataset.completedLoops || 0) + 1); });
          if (animation.isLoaded) container.dataset.iconReady = 'true';
        });
        ready = true;
        root.dataset.animationState = 'ready';
        sync();
      } catch (error) {
        if (active) root.dataset.animationState = 'unavailable';
        console.warn('herbl liver progression:', error.message);
      } finally {
        loading = false;
      }
    }
    const intersection = new IntersectionObserver(entries => {
      visible = entries.some(entry => entry.isIntersecting);
      sync();
    }, {threshold: 0.01});
    intersection.observe(root);
    const disclosure = new MutationObserver(sync);
    if (details) disclosure.observe(details, {subtree: true, attributes: true, attributeFilter: ['open', 'aria-expanded']});
    document.addEventListener('visibilitychange', sync, {signal: abort.signal});
    motionReduced.addEventListener('change', sync, {signal: abort.signal});
    return () => {
      active = false;
      abort.abort();
      intersection.disconnect();
      disclosure.disconnect();
      icons.forEach(({animation}) => animation?.destroy());
    };
  }
  if (!customElements.get('herbl-liver-progression')) customElements.define('herbl-liver-progression', class extends HTMLElement {
    connectedCallback() {
      requestAnimationFrame(() => {
        if (this.isConnected && !this.dispose) this.dispose = mountProgression(this);
      });
    }
    disconnectedCallback() {
      this.dispose?.();
      this.dispose = null;
    }
  });
})();
